/**
 * shell/ResearchChildDashboard.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Per-Child View, now its own page (routed at /research/children) instead
 * of a tab inside ResearchDashboard.jsx. Every variable for one child, its
 * own track, its own unit, plotted against days-since-induction — no
 * computed relationship between tracks, that judgement stays with whoever's
 * looking at it.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { DOMAIN_IDS } from "@/constants/ncfFramework";
import { QS_CLUSTERS } from "@/constants/researchVariables";
import {
  API, Track, CARD_PALETTE,
  koshaSeriesFrom, cardSeriesFrom, moduleSeriesFrom,
} from "./ResearchShared";

// Same thresholds as routes/research.js's flag classification — duplicated
// here only for display, never used to decide what gets fetched or stored.
function srtBandScore(score) {
  if (score == null) return null;
  if (score >= 75) return 3; // achieved
  if (score >= 45) return 2; // developing
  if (score >= 1)  return 1; // emerging
  return null;
}
const QS_FLAG_SCORE = { green: 1, amber: 2, red: 3 };
const QS_REC_SCORE  = { 'no-concerns': 1, monitor: 2, 'full-srt': 3 };

// clusterScores arrives pre-computed per round from the backend (see
// computeClusterScores in routes/research.js) — this just reshapes it into
// one Track-ready series per cluster, the same pattern koshaSeriesFrom uses.
function clusterSeriesFrom(trend) {
  return QS_CLUSTERS.map(cl => ({
    key: cl.id, label: `${cl.icon} ${cl.label}`, color: cl.color,
    points: trend.map(t => ({ x: t.daysSinceInduction, y: t.clusterScores?.[cl.id] })).filter(p => p.y != null),
  })).filter(s => s.points.length);
}

export default function ResearchChildDashboard() {
  const { getAuthHeader } = useAuth();
  const [roster,        setRoster]        = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [enrolledNow,   setEnrolledNow]   = useState(0);
  const [search,        setSearch]        = useState('');
  const [selectedCode,  setSelectedCode]  = useState(null);
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);

  useEffect(() => {
    setRosterLoading(true);
    fetch(`${API}/research/children`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => { if (d.success) { setRoster(d.children); setEnrolledNow(d.enrolledNow || 0); } })
      .catch(() => {}).finally(() => setRosterLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCode) return;
    setLoading(true); setData(null);
    fetch(`${API}/research/children/${selectedCode}`, { headers: getAuthHeader() })
      .then(r => r.json()).then(d => { if (d.success) setData(d); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [selectedCode]);

  const filteredRoster = roster.filter(c => c.researchCode.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ padding: '28px 26px 80px', maxWidth: 1280, margin: '0 auto', fontFamily: "'Inter',system-ui,sans-serif" }}>
      <div style={{ marginBottom: 18 }}>
        <Link to="/research" style={{ fontSize: 12, color: 'var(--mist)', textDecoration: 'none' }}>← Across-Group Dashboard</Link>
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 900, color: 'var(--navy)', marginBottom: 5, lineHeight: 1 }}>
          Per-Child View
        </div>
        <div style={{ fontSize: 13, color: 'var(--mist)' }}>
          Every tracked variable for one child, plotted against days since induction
        </div>
      </div>

      {rosterLoading ? (
        <div style={{ padding: 80, textAlign: 'center', color: '#9CA3AF' }}>
          <div style={{ width: 28, height: 28, margin: '0 auto 14px', border: '3px solid #E5E7EB',
            borderTop: '3px solid var(--navy)', borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>Loading research roster…</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>
            First load can take a few extra seconds while any new children are assigned research codes.
          </div>
        </div>
      ) : (
        <>
          {enrolledNow > 0 && (
            <div style={{ marginBottom: 14, padding: '8px 12px', background: '#F0FDF4', border: '1px solid #BBF7D0',
              borderRadius: 8, fontSize: 12, color: '#166534' }}>
              {enrolledNow} {enrolledNow === 1 ? 'child was' : 'children were'} just assigned a research code for the first time.
            </div>
          )}
          <div style={{ display: 'flex', gap: 18 }}>
            <div style={{ width: 220, flexShrink: 0 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search research code…"
                style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #E5E7EB', borderRadius: 9, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} />
              <div style={{ maxHeight: 560, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 10 }}>
                {filteredRoster.map(c => (
                  <button key={c.researchCode} onClick={() => setSelectedCode(c.researchCode)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px',
                      background: selectedCode === c.researchCode ? '#FFF7ED' : '#fff',
                      border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: '#111827' }}>
                      {c.researchCode}
                      {c.eipReferral?.referred && (
                        <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#DC2626', background: '#FEF2F2', padding: '1px 6px', borderRadius: 10 }}>EIP</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                      {(c.disabilities || []).join(', ') || 'Unclassified'} · {c.ageBand}{!c.isActive ? ' · inactive' : ''}
                    </div>
                  </button>
                ))}
                {filteredRoster.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No matches.</div>
                )}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {!selectedCode ? (
                <div style={{ padding: 60, textAlign: 'center', color: '#9CA3AF' }}>Select a research code to view their trajectory.</div>
              ) : loading ? (
                <div style={{ padding: 60, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
              ) : !data ? null : (
                <>
                  <div style={{ marginBottom: 14, fontSize: 12, color: '#6B7280' }}>
                    Induction date: {new Date(data.inductionDate).toLocaleDateString('en-IN')} — every track below is plotted in days since this date, not calendar date or session index.
                    {data.child?.eipReferral?.referred && (
                      <span style={{ display: 'block', marginTop: 6, color: '#DC2626', fontWeight: 600 }}>
                        Referred to EIP on {new Date(data.child.eipReferral.referredAt).toLocaleDateString('en-IN')}.
                      </span>
                    )}
                  </div>

                  {data.srt.length > 0 && (
                    <Track title="SRT Kosha Score" unit="% per Panchakosha domain"
                      series={koshaSeriesFrom(data.srt, 'koshaScores')} yDomain={[0, 100]} yFormat={v => `${Math.round(v)}%`} />
                  )}
                  {data.srt.length > 0 && (
                    <Track title="SRT Domain Band" unit="Emerging(1) / Developing(2) / Achieved(3)"
                      series={koshaSeriesFrom(data.srt, 'koshaScores', srtBandScore)} yDomain={[0, 3]} />
                  )}
                  {data.quickScreener.length > 0 && (
                    <Track title="QuickScreener Domain Score" unit="% per Panchakosha domain"
                      series={koshaSeriesFrom(data.quickScreener, 'domainScores')} yDomain={[0, 100]} yFormat={v => `${Math.round(v)}%`} />
                  )}
                  {data.quickScreener.length > 0 && (
                    <Track title="QuickScreener Domain Flag" unit="Green(1) / Amber(2) / Red(3)"
                      series={koshaSeriesFrom(data.quickScreener, 'flags', v => QS_FLAG_SCORE[v] ?? null)} yDomain={[0, 3]} />
                  )}
                  {data.quickScreener.length > 0 && (
                    <Track title="QuickScreener Recommendation" unit="No-concern(1) / Monitor(2) / Full-SRT(3)"
                      series={[{ key: 'recommendation', label: 'Recommendation', color: '#16A34A',
                        points: data.quickScreener.map(t => ({ x: t.daysSinceInduction, y: QS_REC_SCORE[t.recommendation] ?? null })).filter(p => p.y != null) }]}
                      yDomain={[0, 3]} />
                  )}
                  {data.quickScreener.length > 0 && (
                    <Track title="QuickScreener Cluster Score" unit="% per functional cluster (11 clusters)"
                      series={clusterSeriesFrom(data.quickScreener)} yDomain={[0, 100]} yFormat={v => `${Math.round(v)}%`} thin />
                  )}

                  {DOMAIN_IDS.map(domainId => {
                    const inds = data.progressTracker.filter(p => p.domainId === domainId);
                    const series = inds.map((p, i) => ({
                      key: p.indicatorId, label: p.indicatorId, color: CARD_PALETTE[i % CARD_PALETTE.length],
                      points: p.history.map(h => ({ x: h.daysSinceInduction, y: h.score })).filter(pt => pt.y != null),
                    })).filter(s => s.points.length);
                    if (!series.length) return null;
                    return <Track key={domainId} title={`Progress Tracker — ${domainId}`} unit="level (0–3)" series={series} yDomain={[0, 3]} />;
                  })}

                  <Track title="BKT Mastery Probability" unit="probability (0–1)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'masteryProb')} yDomain={[0, 1]} />
                  <Track title="BKT Slip Rate" unit="probability (0–1)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'pSlip')} yDomain={[0, 1]} />
                  <Track title="BKT Guess Rate" unit="probability (0–1)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'pGuess')} yDomain={[0, 1]} />
                  <Track title="Engine: Mastered Flag" unit="1 = mastery threshold crossed (≥0.95)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'masteredFlag')} yDomain={[0, 1]} />
                  <Track title="Engine: Guess Alert" unit="1 = alert raised (>0.30)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'guessAlert')} yDomain={[0, 1]} />
                  <Track title="Engine: Slip Alert" unit="1 = alert raised (>0.10)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'slipAlert')} yDomain={[0, 1]} />
                  <Track title="Stuck Alert" unit="1 = flagged stuck (heuristic)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'stuckAlert')} yDomain={[0, 1]} />
                  <Track title="SM-2 Ease Factor" unit="multiplier (floor 1.3)"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'ef')} yDomain={[1.3, 4]} />
                  <Track title="SM-2 Interval" unit="days until next review"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'interval')} />
                  <Track title="Elo Rating Gap" unit="child rating − card difficulty"
                    series={cardSeriesFrom(data.flashcard.cardLevelTrend, 'eloGap')} />
                  <Track title="Module Accuracy" unit="% per session"
                    series={moduleSeriesFrom(data.flashcard.moduleAccuracyTrend)} yDomain={[0, 100]} yFormat={v => `${Math.round(v)}%`} />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}